import { useRef, useState, useEffect, useCallback, useContext } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { Context as GeneralContext } from '@/context'
import usePrevious from '@/hooks/usePrevious'
import useUpdateEffect from '@/hooks/useUpdateEffect'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Form from '@/components/Form'
import RevealGrid from '@/components/RevealGrid'
import TextBlock from '@/components/TextBlock'
import Footer from '@/components/Footer'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import { cutExcerpt, insertBrAfterFirstSpace } from '@/utils/strings'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const Contact = ({
  stub,
}) => {
  const { setPageAnimationReady, setHeaderInverse } = useContext(GeneralContext)
  const classes = useStyles()
  const data = useRef([])
  const [load, setLoad] = useState(false)
  const [feedback, setFeedback] = useState({})

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey, form, response, strings, ready } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub],
    mediaQueryKey: state.mediaQuery.key,
    form: state.page.forms,
    response: state.page.status_form,
    strings: state.language.string,
    ready: state.loading.ready,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const getForm = useCallback((index) => dispatch(actions.page.getDataForm(index)), [dispatch])
  const sendForm = useCallback((index, params) => dispatch(actions.page.sendForm(index, params)), [dispatch])
  const resetForm = useCallback(() => dispatch(actions.page.resetForm()), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Get Data
  ------------------------------*/
  const getData = useCallback(() => {
    if (page !== undefined) {
      data.current = { ...page }
      getForm(data.current.contact_form_id)
    }
  }, [ready, page])

  /*------------------------------
  Have Form
  ------------------------------*/
  const haveForm = () => {
    const { contact_form_id } = data.current
    if (form !== undefined && contact_form_id !== undefined) {
      completePreload()
    }
  }

  /*------------------------------
  Did Update
  ------------------------------*/
  const prevForm = usePrevious(form)
  const prevMediaQueryKey = usePrevious(mediaQueryKey)
  const prevContent = usePrevious(page)

  useUpdateEffect(() => {
    if (prevContent !== page) getData()
    if (prevMediaQueryKey !== mediaQueryKey) getData()
    if (prevForm !== form) haveForm()
  }, [page, mediaQueryKey, form])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) {
      setCurrentPage(stub, 'pages')
    }
    getPage(stub)
    getData()
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Set Header Logo Styles
  ------------------------------*/
  useEffect(() => {
    setHeaderInverse(true)
    return () => setHeaderInverse(false)
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Heading
  ------------------------------*/
  const renderHeading = () => {
    return (
      <Hero
        aboveTheFold
        viewportHeight={false}
        background="light"
        fontSize="md"
        text={cutExcerpt(page.content.rendered)}
        title={insertBrAfterFirstSpace(page.title.rendered)}
        imgSize="crop"
        onComplete={() => setPageAnimationReady(true)}
      />
    )
  }

  /*------------------------------
  Callback Form
  ------------------------------*/
  const callbackForm = (value) => {
    sendForm(data.current.contact_form_id, value)
  }

  /*------------------------------
  Risposta form
  ------------------------------*/
  useUpdateEffect(() => {
    setFeedback(response)
  }, [response])

  /*------------------------------
  Render Form
  ------------------------------*/
  const renderForm = () => {
    const { contact_form_id, contact_form_text, contact_form_btn } = data.current
    const contactForm = form[contact_form_id]

    return (
      <RevealGrid cols={1}>
        <div className={classes.wrapper}>
          <TextBlock
            text={contact_form_text}
          />
        </div>
        <div className={`${classes.wrapper}`}>
          <div className={`${classes.formContent} ${classes.formContentCenter}`}>
            <Form
              data={contactForm.data}
              newsletter={strings.acceptance_newsletter}
              privacy={strings.acceptance_privacy_text}
              privacyLink={strings.acceptance_privacy_link}
              labelSubmit={contact_form_btn}
              resetForm={resetForm}
              sendForm={callbackForm}
              parent={this}
              response={feedback}
            />
          </div>
        </div>
      </RevealGrid>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer noMargin />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHeading()}
        {renderForm()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageContact ${classes.root}`}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Contact
